// libraries
import React, { useRef, useState, useEffect, useCallback } from 'react'
import clsx from 'clsx'
import { useFormContext, RegisterOptions } from 'react-hook-form'

// utils
import { isNonWhitespaceString, useAnnounceOwnError } from '/lib/reactutils/utils'

// svg
import { Warning } from '/react4xp/components/Svg/Ux/Ux'
import { AngleDown } from '/react4xp/components/Svg/Ux/Angle'

interface SelectProps {
    id: string
    name: string
    label?: string
    required?: boolean
    className?: string
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
    defaultValue: string
    options: Array<{
        value: string
        label: string
        disabled?: boolean
    }>
    errorMessages?: {
        required?: string
    }
    helperText?: string
    onOptionSelect?: (value: string) => void
}

export default function Select({
    id,
    name,
    label,
    required,
    className,
    onChange = () => {},
    defaultValue,
    options,
    errorMessages,
    helperText,
    onOptionSelect,
}: SelectProps) {

    const formContext = useFormContext()
    const { register, setValue, trigger, formState } = formContext || {}
    const errors = formState?.errors || {}

    let validations: RegisterOptions = {
        onChange: (e) => onChange(e)
    }

    if (required) {
        validations.required = errorMessages?.required
    }

    const errorMessage = errors && errors[name]?.message
    const announceError = useAnnounceOwnError(Boolean(errorMessage), formState?.submitCount || 0)

    const showHelperText = isNonWhitespaceString(helperText)
    const labelId = `${id}-label`
    const listboxId = `${id}-listbox`
    const helperTextId = `${id}-helper-text`
    const errorMessageId = `${id}-error-message`
    const optionId = (index: number) => `${id}-option-${index}`

    const describedBy = [
        showHelperText && helperTextId,
        errorMessage && errorMessageId
    ].filter(Boolean).join(' ') || undefined

    // fake select dropdown
	const [isDropdownVisible, setIsDropdownVisible] = useState(false)
    const [selectedValue, setSelectedValue] = useState('')
    const buttonRef = useRef<HTMLButtonElement>(null)

    // bind a function on click to close the fake select dropdown if clicked outside
	const dropdownRef = useRef(null)

    // add new state for search and currently active option
    const [searchTerm, setSearchTerm] = useState('')
    const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null)
    const [activeOptionIndex, setActiveOptionIndex] = useState<number>(-1)

    const closeDropdown = useCallback(() => {
        setIsDropdownVisible(false)
        setActiveOptionIndex(-1)
    }, [])

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				closeDropdown()
			}
		}

		document.addEventListener('mousedown', handleClickOutside)

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [closeDropdown])

    // on submit the form focuses the field it registered, which here is the hidden value
    // input and cannot take focus, so redirect that to the button. Only on submit: doing it
    // whenever the field turns invalid traps the keyboard on tab out of an empty one
    const submitCount = formState?.submitCount || 0

    useEffect(() => {
        if (submitCount === 0 || !errorMessage || selectedValue) {
            return
        }

        // and only when no earlier field in error has already taken it
        const focused = document.activeElement

        if (focused !== buttonRef.current && focused?.hasAttribute('name')) {
            return
        }

        buttonRef.current?.focus()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitCount])

    // the options only exist while the list is open, so opening it scrolls back to the
    // current choice rather than to the top, and the keyboard keeps its option in view
    useEffect(() => {
        if (!isDropdownVisible) {
            return
        }

        const index = activeOptionIndex !== -1
            ? activeOptionIndex
            : options?.findIndex(option => option.label === selectedValue)

        if (index !== undefined && index !== -1) {
            document.getElementById(optionId(index))?.scrollIntoView({ block: 'nearest' })
        }
    }, [isDropdownVisible, activeOptionIndex, options, selectedValue, id])

    const selectOption = useCallback((index: number) => {
        const item = options?.[index]

        if (!item || item.disabled) {
            return
        }

        closeDropdown()
        setValue(name, item.label)
        setSelectedValue(item.label)
        trigger(name)
        onOptionSelect?.(item.label)
        buttonRef.current?.focus()
    }, [options, closeDropdown, setValue, name, trigger, onOptionSelect])

    const findSelectable = useCallback((from: number, step: number) => {
        for (let i = from; i >= 0 && i < (options?.length || 0); i += step) {
            if (!options[i].disabled) {
                return i
            }
        }

        return -1
    }, [options])

    // opening with the pointer leaves the list without an active option, the way it
    // reads and looks today; the keyboard paths pass the option they land on
    const openDropdown = useCallback((from = -1, step = 1) => {
        setIsDropdownVisible(true)
        setActiveOptionIndex(from === -1 ? -1 : findSelectable(from, step))
    }, [findSelectable])

    const moveActiveOption = useCallback((from: number, step: number) => {
        const index = findSelectable(from, step)

        if (index !== -1) {
            setActiveOptionIndex(index)
        }
    }, [findSelectable])

    const currentIndex = activeOptionIndex !== -1
        ? activeOptionIndex
        : options?.findIndex(option => option.label === selectedValue) ?? -1

    const typeAhead = useCallback((key: string) => {
        // append to the existing search term if within the timeout
        const newSearchTerm = (searchTimeout ? searchTerm + key : key).toLowerCase()
        setSearchTerm(newSearchTerm)

        if (searchTimeout) {
            clearTimeout(searchTimeout)
        }

        const timeout = setTimeout(() => {
            setSearchTerm('')
            setSearchTimeout(null)
        }, 500)
        setSearchTimeout(timeout)

        const matchingOptionIndex = options?.findIndex(option =>
            !option.disabled && option.label.toLowerCase().startsWith(newSearchTerm)
        )

        setIsDropdownVisible(true)

        if (matchingOptionIndex !== undefined && matchingOptionIndex !== -1) {
            setActiveOptionIndex(matchingOptionIndex)
        }
    }, [searchTerm, searchTimeout, options])

    // add keyboard navigation handler
    const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
        const lastIndex = (options?.length || 0) - 1

        switch (event.key) {
            case 'Escape':
                if (isDropdownVisible) {
                    event.preventDefault()
                    closeDropdown()
                }
                return

            case 'Tab':
                if (isDropdownVisible) {
                    closeDropdown()
                }
                return

            case 'ArrowDown':
                event.preventDefault()
                isDropdownVisible
                    ? moveActiveOption(Math.min(currentIndex + 1, lastIndex), 1)
                    : openDropdown(Math.max(currentIndex, 0), 1)
                return

            case 'ArrowUp':
                event.preventDefault()
                isDropdownVisible
                    ? moveActiveOption(Math.max(currentIndex - 1, 0), -1)
                    : openDropdown(currentIndex === -1 ? lastIndex : currentIndex, -1)
                return

            case 'Home':
                event.preventDefault()
                isDropdownVisible ? moveActiveOption(0, 1) : openDropdown(0, 1)
                return

            case 'End':
                event.preventDefault()
                isDropdownVisible ? moveActiveOption(lastIndex, -1) : openDropdown(lastIndex, -1)
                return

            case 'Enter':
                event.preventDefault()
                if (!isDropdownVisible) {
                    openDropdown(Math.max(currentIndex, 0), 1)
                } else if (activeOptionIndex !== -1) {
                    selectOption(activeOptionIndex)
                } else {
                    closeDropdown()
                }
                return
        }

        // space opens and picks like a native select, but keeps feeding a search already in progress
        if (event.key === ' ' && !searchTerm) {
            event.preventDefault()
            isDropdownVisible && activeOptionIndex !== -1
                ? selectOption(activeOptionIndex)
                : openDropdown(Math.max(currentIndex, 0), 1)
            return
        }

        // only handle printable characters
        if (event.key.length === 1) {
            event.preventDefault()
            typeAhead(event.key)
        }
    }, [
        options,
        isDropdownVisible,
        currentIndex,
        activeOptionIndex,
        searchTerm,
        closeDropdown,
        openDropdown,
        moveActiveOption,
        selectOption,
        typeAhead
    ])

    // Add cleanup for timeout
    useEffect(() => {
        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout)
            }
        }
    }, [searchTimeout])

    // listen for form reset event
    useEffect(() => {
        const handleFormReset = () => {
            setSelectedValue('')
            setIsDropdownVisible(false)
            setActiveOptionIndex(-1)
            setSearchTerm('')
            if (setValue) {
                setValue(name, '')
            }
        }

        document.addEventListener('resetForm', handleFormReset)

        return () => {
            document.removeEventListener('resetForm', handleFormReset)
        }
    }, [name, setValue])

    return (
        <div className={clsx(
            'form-line',
            className,
            errors && errors[name] && 'form-line--error'
        )}>

            <label id={labelId} className='form-line__label text-md' htmlFor={id}>
                {label} {required && <span className='form-line__label__required text-sm'>(Required)</span>}
            </label>

            <div
                className={clsx(
                    'form-line__line-wrapper',
                    isDropdownVisible && 'active'
                )}
                ref={dropdownRef}
            >

                <div className='select-wrapper'>

                    <button
                        ref={buttonRef}
                        id={id}
                        name={name}
                        type='button'
                        role='combobox'
                        className={clsx(
                            'form-line__toggle-select text-md',
                            selectedValue !== '' && 'invisible'
                        )}
                        aria-haspopup='listbox'
                        aria-expanded={isDropdownVisible}
                        aria-controls={listboxId}
                        aria-labelledby={`${labelId} ${id}`}
                        aria-activedescendant={
                            isDropdownVisible && activeOptionIndex !== -1
                                ? optionId(activeOptionIndex)
                                : undefined
                        }
                        aria-required={required || undefined}
                        aria-invalid={Boolean(errorMessage)}
                        aria-describedby={describedBy}
                        onClick={() => isDropdownVisible ? closeDropdown() : openDropdown()}
                        onBlur={() => selectedValue === '' && trigger(name)}
                        onKeyDown={handleKeyDown}
                    >
                        {selectedValue || defaultValue || 'Select an option'}
                    </button>

                    <input
                        type='text'
                        name={name}
                        className='form-line__select text-md'
                        value={selectedValue}
                        disabled
                        aria-hidden='true'
                        onChange={onChange}
                        tabIndex={-1}
                        {...(register ? register(name || '', validations) : {})}
                    />

                    <div className='form-line__side-icon'>
                        <AngleDown />
                    </div>

                </div>

                <div
                    id={listboxId}
                    className='select-options'
                    role='listbox'
                    aria-labelledby={labelId}
                >
                    {isDropdownVisible && options?.map((item, i) => (
                        <button
                            key={i}
                            id={optionId(i)}
                            className={clsx(
                                'select-option',
                                activeOptionIndex === i && 'focused'
                            )}
                            type='button'
                            role='option'
                            aria-selected={selectedValue === item.label}
                            tabIndex={-1}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => selectOption(i)}
                            disabled={item.disabled}
                        >
                            {item.label}
                        </button>
                    ))}

                </div>

            </div>

            {showHelperText && (
                <div
                    id={helperTextId}
                    className='form-line__help-text text-sm'
                    dangerouslySetInnerHTML={{ __html: helperText }}
                    suppressHydrationWarning
                />
            )}

            {errorMessage && (
                <p
                    id={errorMessageId}
                    role={announceError ? 'alert' : undefined}
                    className='
                        form-line__error-message
                        form-line__error-message--input
                        labels-sm-regular
                    '
                >
                    <Warning />
                    {typeof errorMessage === 'string' && errorMessage}
                </p>
            )}

        </div>
    )
}
